import Axios, {type AxiosError, type AxiosRequestConfig} from "axios";
import * as React from "react";
import type {User} from "@/api/model";
import {refresh} from "@/api/authentication/authentication.ts";

export const AXIOS_INSTANCE = Axios.create({
    baseURL: import.meta.env.DEV ? "/proxyroute" : "https://api.app.firma-stockner.com",
});

export const JWT_TOKEN_KEY = "api-token";
export const JWT_REFRESH_TOKEN_KEY = "api-refresh-token";

export function setToken(token: string | undefined) {
    if (token) {
        localStorage.setItem(JWT_TOKEN_KEY, token);
    } else {
        localStorage.removeItem(JWT_TOKEN_KEY);
    }
}

export function setRefreshToken(token: string | undefined) {
    if (token) {
        localStorage.setItem(JWT_REFRESH_TOKEN_KEY, token);
    } else {
        localStorage.removeItem(JWT_REFRESH_TOKEN_KEY);
    }
}

AXIOS_INSTANCE.interceptors.request.use(
    (config) => {
        if (config.url?.includes("auth/refresh")) {
            return config;
        }
        const token = localStorage.getItem(JWT_TOKEN_KEY);
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error) => Promise.reject(error),
);


export const setUserDispatch: {
    current: React.Dispatch<React.SetStateAction<User | undefined>> | undefined
} = {
    current: undefined
}

function logout() {
    if (setUserDispatch.current) {
        setUserDispatch.current(undefined);
    }
    setToken(undefined);
    setRefreshToken(undefined);
}

AXIOS_INSTANCE.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.response.request?.responseURL?.includes("auth/refresh")) {
            const token = localStorage.getItem(JWT_REFRESH_TOKEN_KEY);
            if (token) {
                refresh({headers: {"Authorization": `Bearer ${token}`}}).then((res) => {
                    setToken(res.accessToken);
                    setRefreshToken(res.refreshToken);
                    if (setUserDispatch.current) {
                        setUserDispatch.current(res.user)
                    }
                }, logout);
            } else {
                logout();
            }
            // Handle unauthorized
        }
        return Promise.reject(error);
    },
);

export const customInstance = <T>(
    config: AxiosRequestConfig,
    options?: AxiosRequestConfig,
): Promise<T> => {
    return AXIOS_INSTANCE({
        ...config,
        ...options,
    }).then(({data}) => data);
};
export type ErrorType<Error> = AxiosError<Error>;
export type BodyType<BodyData> = BodyData;
export {logout}
